import { useCallback, useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Grid2, Box, TextField } from "@mui/material";
import StyledGridComp from "../../components/StyledGridComp";
import { notifySuccess, notifyError } from "../../utilities/helper";
import { Home } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import SearchableSelect from "../../components/SearchableSelect";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Loader from "../../components/Loader";
import {
    fetchExportGridService,
    createExportService,
    runExportService,
    downloadExportService,
    fetchPatientsBySiteService,
} from "../../services/exportService";
import { fetchAllSiteDataService } from "../../services/subjectService";

const Export = () => {
    const [load, setLoad] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(15);

    const [rowData, setRowData] = useState([]);
    const [siteOptions, setSiteOptions] = useState([]);
    const [patientOptions, setPatientOptions] = useState([{ id: "", patientNumber: "All Patients" }]);

    const access_token = localStorage.getItem("access_token");
    const userId       = localStorage.getItem("roleId");
    const email        = localStorage.getItem("email") || "";

    const loadPatients = useCallback((siteId) => {
        fetchPatientsBySiteService(access_token, siteId || null)
            .then((res) => {
                const patients = res?.data?.result ?? [];
                setPatientOptions([{ id: "", patientNumber: "All Patients" }, ...patients]);
            })
            .catch(() => notifyError("Failed to load patients."));
    }, [access_token]);

    // Load sites + all patients on mount
    useEffect(() => {
        if (!userId || !access_token) return;
        fetchAllSiteDataService(access_token, userId)
            .then((res) => {
                const sites = res?.data?.result ?? [];
                setSiteOptions(sites);
            })
            .catch(() => notifyError("Failed to load sites."));
        loadPatients(null);
    }, [userId, access_token]);

    // Load export grid on mount
    const loadGrid = useCallback(() => {
        if (!userId) return;
        setLoad(true);
        fetchExportGridService(access_token, userId)
            .then((res) => {
                setRowData(res?.data?.result ?? []);
            })
            .catch(() => notifyError("Failed to load exports."))
            .finally(() => setLoad(false));
    }, [access_token, userId]);

    useEffect(() => { loadGrid(); }, [loadGrid]);

    // 20s silent auto-refresh so status changes (In Queue -> Processing -> Complete) appear (BR-14)
    useEffect(() => {
        if (!userId) return;
        const id = setInterval(() => {
            fetchExportGridService(access_token, userId)
                .then((res) => setRowData(res?.data?.result ?? []))
                .catch(() => {});
        }, 20000);
        return () => clearInterval(id);
    }, [access_token, userId]);

    // When site changes, reload patients filtered by that site (or all if cleared)
    const handleSiteChange = useCallback((siteId) => {
        loadPatients(siteId || null);
    }, [loadPatients]);

    const handleDownload = async (row) => {
        try {
            const res = await downloadExportService(access_token, row.id);
            const url  = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href  = url;
            // Prefer the server-provided filename (Content-Disposition); fall back to a .zip name.
            const cd = res.headers?.["content-disposition"] || "";
            const match = cd.match(/filename="?([^"]+)"?/);
            link.setAttribute("download", match ? match[1] : `${row.exportName}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            notifyError("Download failed. The export archive may not be ready yet.");
        }
    };

    const handleRunExport = async (row) => {
        setRowData((curr) =>
            curr.map((item) =>
                item.id === row.id ? { ...item, exportStatus: "Processing", completedTime: "-" } : item
            )
        );
        try {
            const res = await runExportService(access_token, row.id);
            if (res?.data?.status !== "200") {
                notifyError(res?.data?.message || "Run export failed.");
                loadGrid();
                return;
            }
            notifySuccess(`Export "${row.exportName}" run started.`);
            // status converges via the 20s auto-refresh
        } catch {
            notifyError("Run export failed.");
            loadGrid();
        }
    };

    const columns = useMemo(() => [
        {
            field: "exportName",
            headerName: "Export Name",
            width: 180,
            renderHeader: () => <div className="grid-heading">Export Name</div>,
            renderCell: (p) => <span className="data-font">{p.row.exportName}</span>,
        },
        {
            field: "siteName",
            headerName: "Site Name",
            width: 150,
            renderHeader: () => <div className="grid-heading">Site Name</div>,
            renderCell: (p) => <span className="data-font">{p.row.siteName}</span>,
        },
        {
            field: "siteNumber",
            headerName: "Site Number",
            width: 120,
            renderHeader: () => <div className="grid-heading">Site Number</div>,
            renderCell: (p) => <span className="data-font">{p.row.siteNumber}</span>,
        },
        {
            field: "patientNumber",
            headerName: "Patient",
            width: 130,
            renderHeader: () => <div className="grid-heading">Patient</div>,
            renderCell: (p) => <span className="data-font">{p.row.patientNumber}</span>,
        },
        {
            field: "from",
            headerName: "From",
            width: 180,
            renderHeader: () => <div className="grid-heading">From</div>,
            renderCell: (p) => <span className="data-font">{p.row.from}</span>,
        },
        {
            field: "to",
            headerName: "To",
            width: 180,
            renderHeader: () => <div className="grid-heading">To</div>,
            renderCell: (p) => <span className="data-font">{p.row.to}</span>,
        },
        {
            field: "exportStatus",
            headerName: "Export Status",
            width: 150,
            renderHeader: () => <div className="grid-heading">Export Status</div>,
            renderCell: (p) => {
                const status = p.row.exportStatus;
                const color  = status === "Complete" ? "green"
                    : status === "Processing" ? "#00b0f0"
                    : status === "Failed" ? "red"
                    : "orange"; // In Queue / Removed
                return <span className="data-font" style={{ color, fontWeight: "bold" }}>{status}</span>;
            },
        },
        {
            field: "createdTime",
            headerName: "Created Time",
            width: 180,
            renderHeader: () => <div className="grid-heading">Created Time</div>,
            renderCell: (p) => <span className="data-font">{p.row.createdTime}</span>,
        },
        {
            field: "startedTime",
            headerName: "Started Time",
            width: 180,
            renderHeader: () => <div className="grid-heading">Started Time</div>,
            renderCell: (p) => <span className="data-font">{p.row.startedTime}</span>,
        },
        {
            field: "completedTime",
            headerName: "Completed Time",
            width: 180,
            renderHeader: () => <div className="grid-heading">Completed Time</div>,
            renderCell: (p) => <span className="data-font">{p.row.completedTime}</span>,
        },
        {
            field: "action",
            headerName: "Action",
            width: 220,
            renderHeader: () => <div className="grid-heading">Action</div>,
            renderCell: (p) => {
                const status = p.row.exportStatus;
                const isComplete = status === "Complete";
                const isBusy = status === "In Queue" || status === "Processing";
                return (
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        {isComplete && (
                            <>
                                <span
                                    style={{ color: "#00b0f0", cursor: "pointer", fontWeight: "bold" }}
                                    onClick={() => handleDownload(p.row)}
                                >
                                    Download
                                </span>
                                <span style={{ color: "#aaa" }}>|</span>
                            </>
                        )}
                        {!isBusy && (
                            <span
                                style={{ color: "#00b0f0", cursor: "pointer", fontWeight: "bold" }}
                                onClick={() => handleRunExport(p.row)}
                            >
                                Run Export
                            </span>
                        )}
                    </div>
                );
            },
        },
    ], [rowData]);

    const createExportSchema = yup.object().shape({
        exportName: yup.string().required("Export Name is required"),
        site:       yup.string().required("Site is required"),
        patient:    yup.string(),
        fromDate:   yup.mixed().nullable(),
        toDate:     yup.mixed().nullable(),
    });

    const {
        register,
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(createExportSchema),
        defaultValues: { exportName: "", site: "", patient: "", fromDate: null, toDate: null },
    });

    const watchedSite = watch("site");

    useEffect(() => {
        handleSiteChange(watchedSite || null);
    }, [watchedSite, handleSiteChange]);

    const onSubmit = async (data) => {
        setLoad(true);
        try {
            const payload = {
                exportName: data.exportName,
                userId:     userId,
                siteId:     data.site     || null,
                patientId:  data.patient  || null,
                // date-only (avoids timezone day-shift); backend normalizes to start/end of day
                fromDate:   data.fromDate ? dayjs(data.fromDate).format("YYYY-MM-DD") : null,
                toDate:     data.toDate   ? dayjs(data.toDate).format("YYYY-MM-DD")   : null,
            };
            const res = await createExportService(access_token, payload);
            if (res?.data?.status !== "200") {
                notifyError(res?.data?.message || "Failed to create export.");
                return;
            }
            notifySuccess("Export created successfully!");
            reset({ exportName: "", site: "", patient: "", fromDate: null, toDate: null });
            loadGrid();
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to create export.";
            notifyError(msg);
        } finally {
            setLoad(false);
        }
    };

    return (
        <div className="content-body">
            {load && <Loader />}
            <div className="home-container">
                <p className="bread-crumbs-link-container" style={{ fontWeight: "600" }}>
                    <Link className="bread-crumbs-link" to="/">Home</Link>
                    {" "}/{" "}
                    <span style={{ color: "gray" }}>Data Export</span>
                </p>
                <Link className="bread-crumbs-link home-inner-container">
                    Set as Home Page
                    <span><Home sx={{ fontSize: "20px" }} /></span>
                </Link>
            </div>

            <Grid2 container alignItems="center" justifyContent="space-between"
                sx={{ width: "100%", margin: "0 auto", marginBottom: "20px" }}>
                <Grid2 item md={12}>
                    <p className="page-heading">Create New Export</p>
                </Grid2>
            </Grid2>

            {/* Export Form */}
            <Box
                component="form"
                autoComplete="off"
                sx={{
                    height: "auto", width: "100%", marginBottom: "30px", padding: "20px",
                    backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0px 2px 4px rgba(0,0,0,0.1)"
                }}
                onSubmit={handleSubmit(onSubmit)}
            >
                <Grid2 container alignItems="flex-start" justifyContent="flex-start"
                    sx={{ width: "100%", margin: "0 auto" }} gap={3}>

                    {/* Column 1 */}
                    <Grid2 size={{ xl: 5.5, md: 5.5, lg: 5.5, sm: 12, xs: 12 }}>
                        <div style={{ marginBottom: "15px" }}>
                            <p className="labelStyles">Export Name <span>*</span></p>
                            <TextField size="small" fullWidth type="text"
                                placeholder="New Export" {...register("exportName")} />
                            {errors.exportName && (
                                <div className="error-container">{errors.exportName.message}</div>
                            )}
                        </div>

                        <div style={{ marginBottom: "15px" }}>
                            <p className="labelStyles">User</p>
                            <TextField size="small" disabled fullWidth type="text"
                                value={email} sx={{ backgroundColor: "#f0f0f0" }} />
                        </div>

                        <div style={{ marginBottom: "15px" }}>
                            <p className="labelStyles">Site <span>*</span></p>
                            <SearchableSelect
                                control={control}
                                data={siteOptions}
                                name="site"
                                optionKey="id"
                                optionLabel="name"
                            />
                            {errors.site && (
                                <div className="error-container">{errors.site.message}</div>
                            )}
                        </div>
                    </Grid2>

                    {/* Column 2 */}
                    <Grid2 size={{ xl: 5.5, md: 5.5, lg: 5.5, sm: 12, xs: 12 }}>
                        <div style={{ marginBottom: "15px" }}>
                            <p className="labelStyles">Patient</p>
                            <SearchableSelect
                                control={control}
                                data={patientOptions}
                                name="patient"
                                optionKey="id"
                                optionLabel="patientNumber"
                            />
                        </div>

                        <div style={{ marginBottom: "15px" }}>
                            <p className="labelStyles">From</p>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Controller name="fromDate" control={control}
                                    render={({ field }) => (
                                        <DatePicker sx={{ width: "100%" }}
                                            value={field.value ? dayjs(field.value) : null}
                                            onChange={(date) => field.onChange(date)}
                                            format="DD-MMM-YYYY"
                                            slotProps={{
                                                textField: {
                                                    placeholder: "Select Date",
                                                    sx: { "& .MuiInputBase-root": { height: "40px" } },
                                                    size: "small", fullWidth: true,
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </div>

                        <div style={{ marginBottom: "15px" }}>
                            <p className="labelStyles">To</p>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Controller name="toDate" control={control}
                                    render={({ field }) => (
                                        <DatePicker sx={{ width: "100%" }}
                                            value={field.value ? dayjs(field.value) : null}
                                            onChange={(date) => field.onChange(date)}
                                            format="DD-MMM-YYYY"
                                            slotProps={{
                                                textField: {
                                                    placeholder: "Select Date",
                                                    sx: { "& .MuiInputBase-root": { height: "40px" } },
                                                    size: "small", fullWidth: true,
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </div>
                    </Grid2>
                </Grid2>

                <div style={{ marginTop: "10px" }}>
                    <button type="submit" className="create-btn"
                        style={{ padding: "8px 25px", display: "flex", alignItems: "center", gap: "5px" }}>
                        + Create
                    </button>
                </div>
            </Box>

            {/* Completed Exports Table */}
            <div style={{ marginTop: "30px" }}>
                <div style={{
                    display: "flex", borderBottom: "2px solid #ccc", marginBottom: "15px"
                }}>
                    <div style={{
                        display: "flex", alignItems: "center", backgroundColor: "#fff",
                        border: "1px solid #ccc", borderBottom: "2px solid #fff",
                        marginBottom: "-2px", padding: "8px 20px", borderRadius: "8px 8px 0 0",
                        fontWeight: "bold", color: "#00b0f0", fontSize: "14px", cursor: "default"
                    }}>
                        <span style={{
                            backgroundColor: "#00b0f0", color: "#fff", borderRadius: "10px",
                            padding: "2px 8px", marginRight: "8px", fontSize: "11px", fontWeight: "bold"
                        }}>{rowData.length}</span>
                        Completed Exports
                    </div>
                </div>

                <StyledGridComp
                    rows={rowData}
                    columns={columns}
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    length={rowData.length}
                    isPaginationClientSide={true}
                />
            </div>
        </div>
    );
};

export default Export;
